<template>
  <v-card v-if="donation && donation.paymentSpecification" class="paylink-payment-card" flat>
    <v-card-title class="paylink-title pa-0">
      {{ t('paylink.paymentTitle') }}
    </v-card-title>
    <v-card-subtitle class="pa-0 mb-2 mt-4">
      {{ t('payment.selectPaymentMethodPlaceholder') }}
    </v-card-subtitle>
    <v-card-text class="px-0">
      <v-row justify="center" class="paylink-button-row">
        <v-col cols="12" md="6" class="paylink-button-col px-0">
          <v-btn
            class="paylink-action-btn"
            :loading="loadingCard"
            :disabled="loadingSwish"
            :aria-label="t('aria.label.payByCard')"
            color="primary"
            data-cy="payment-method-goto-payment-card-btn"
            @click="subPaymentMethods.card && goToPayment(subPaymentMethods.card)"
            block
            flat
          >
            {{ t('paylink.card') }}
            <v-icon class="ml-2" color="white">
              {{ subPaymentMethods.card ? subPaymentMethods.card.icon : '' }}
            </v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" md="6" class="paylink-button-col">
          <v-btn
            class="paylink-action-btn"
            :loading="loadingSwish"
            :disabled="loadingCard"
            :aria-label="t('aria.label.payBySwish')"
            color="primary"
            data-cy="payment-method-goto-payment-swish-btn"
            @click="subPaymentMethods.swish && goToPayment(subPaymentMethods.swish)"
            block
            flat
          >
            {{ t('paylink.swish') }}
            <v-icon class="ml-2" color="white">
              {{ subPaymentMethods.swish ? subPaymentMethods.swish.icon : '' }}
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text class="px-0">
      <v-card class="paylink-certificate-card" flat>
        <v-img
          data-cy="paylink-gift-certificate-image"
          :src="getGiftCertificateUrl()"
          lazy-src="https://picsum.photos/128/?blur=5"
          contain
          width="100%"
          class="grey lighten-2"
        />
      </v-card>
    </v-card-text>

    <v-card-actions class="px-0">
      <v-spacer />
      <v-dialog v-model="dialog" width="500">
        <template #activator="{ props }">
          <v-btn data-cy="paylink-about-btn" v-bind="props" @click="reset">
            <img
              class="paylink-about-logo"
              alt="Min gåva"
              src="@/assets/min_gava_logo.png"
            />
            {{ t('paylink.about') }}
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="paylink-about-header pt-3">
            <span class="paylink-about-title">
              {{ t('paylink.aboutSystem.title') }}
            </span>
            <v-btn icon @click="dialog = false" aria-label="Close" variant="text">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-4">
            <div v-html="t('paylink.aboutSystem.description')"></div>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              data-cy="close-paylink-about-dialog"
              color="primary"
              variant="outlined"
              class="white--text"
              density="comfortable"
              @click="dialog = false"
            >
              {{ t('generic.labels.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="linkExpired" persistent width="500">
        <v-card>
          <v-card-title class="headline primary white--text text--lighten-5">
            <span>{{ t('paylink.linkExpired.title') }}</span>
          </v-card-title>

          <v-card-text class="pa-4">{{ t('paylink.linkExpired.description') }}</v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="alreadyPaid" persistent width="500">
        <v-card>
          <v-card-title class="headline primary white--text text--lighten-5">
            <span>{{ t('paylink.alreadyPaid.title') }}</span>
          </v-card-title>

          <v-card-text class="pa-4">{{ t('paylink.alreadyPaid.description') }}</v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="generalError" persistent width="500">
        <v-card>
          <v-card-title class="headline primary white--text text--lighten-5">
            <span>{{ t('paylink.error.generalError') }}</span>
          </v-card-title>

          <v-card-text class="pa-4">{{ t('paylink.error.validResponseCodeMissing') }}</v-card-text>
        </v-card>
      </v-dialog>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import store from '@/store';
import { ENDPOINTS, get, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { useCertificateOperations } from '@/composables/useCertificateOperations';
import { Donation } from '@/interfaces/donation/donation';
import { PaymentMethod, Type } from '@/interfaces/economy/paymentMethod';
import { Snackbar } from '@/interfaces/presentation/snackbar';
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  name: 'PaylinkView',
  emits: ['reset'],
  setup(_props, { emit }) {
    const { t } = useLabel();
    const { getCertificateUrl } = useCertificateOperations();

    const redirecting = ref(false);
    const loadingSwish = ref(false);
    const loadingCard = ref(false);
    const subPaymentMethods = ref({
      swish: null as PaymentMethod | null,
      card: null as PaymentMethod | null,
    });
    const paymentLoopBreakerThreshold = ref(10);
    const numberOfPaymentAttempts = ref(0);
    const paymentCode = ref<string | null>('');
    const responseCode = ref(200);
    const dialog = ref(false);
    const linkExpired = ref(false);
    const alreadyPaid = ref(false);
    const generalError = ref(false);
    const donation = computed(() => store.state.donation as Donation);
    onMounted(async () => {
      paymentCode.value = getPaymentCodeParam();
      responseCode.value = getResponseCodeParam();

      if (responseCode.value === 410) {
        linkExpired.value = true;
      }

      if (responseCode.value === 406) {
        alreadyPaid.value = true;
      }

      numberOfPaymentAttempts.value = 0;

      const paymentMethods: PaymentMethod[] = await get(ENDPOINTS.PAYMENT_METHODS);

      if (paymentMethods && paymentMethods.length) {
        const card: PaymentMethod | undefined = paymentMethods.find((pm) => pm.type === Type.CARD);
        const swish: PaymentMethod | undefined = paymentMethods.find(
          (pm) => pm.type === Type.SWISH
        );
        if (card) {
          subPaymentMethods.value.card = card;
        }
        if (swish) {
          subPaymentMethods.value.swish = swish;
        }
      }
    });

    const reset = () => {
      emit('reset');
    };

    const getPaymentCodeParam = (): string | null => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      if (urlParams.has('paymentCode')) {
        return urlParams.get('paymentCode');
      }
      return null;
    };

    const getResponseCodeParam = (): number => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      if (urlParams.has('responseCode')) {
        const responseCodeParam: string | null = urlParams.get('responseCode');
        if (responseCodeParam) {
          return Number.parseInt(responseCodeParam);
        } else {
          return 400;
        }
      }
      return 400;
    };

    const getGiftCertificateUrl = (): string => {
      return getCertificateUrl(donation.value.serialnumber, true);
    };

    const goToPayment = (paymentMethod: PaymentMethod) => {
      // create digital payment
      if (paymentMethod.type == Type.SWISH) {
        loadingSwish.value = true;
      }
      if (paymentMethod.type == Type.CARD) {
        loadingCard.value = true;
      }
      get(ENDPOINTS.PAYMENTS + '/' + ENDPOINTS.TRANSACTIONS + '/' + ENDPOINTS.CODE, {
        paymentMethodId: paymentMethod.id,
        code: paymentCode.value,
      })
        .then((response) => {
          if (response.redirectUrl) {
            location.href = response.redirectUrl;
          } else {
            if (numberOfPaymentAttempts.value > paymentLoopBreakerThreshold.value) {
              generalError.value = true;
              loadingCard.value = false;
              loadingSwish.value = false;
            } else {
              // the previous transaction might have failed and we need to create a new one
              numberOfPaymentAttempts.value++;
              goToPayment(paymentMethod);
            }
          }
        })
        .catch((error) => {
          window.log?.error('[Guide] paylink.payment — Paylink payment failed', {
            message: guideErrorMessage(error),
            httpStatus: guideHttpStatus(error),
            serialnumber: donation.value?.serialnumber,
          });
          loadingCard.value = false;
          loadingSwish.value = false;
          generalError.value = true;
          store.commit('setSnackbar', {
            active: true,
            color: 'error',
            text: 'An unexpected error occurred while processing the payment',
          });
          const errorMsg = error.response.data.msg
            ? error.response.data.msg
            : t('paylink.error.generalErrorLinkInvalid');

          const snackbar: Snackbar = {
            active: true,
            color: 'error',
            text: errorMsg,
            timeout: 10000,
            action: null,
          };
          store.commit('setSnackbar', snackbar);
        });
    };

    return {
      t,
      redirecting,
      loadingSwish,
      loadingCard,
      subPaymentMethods,
      paymentLoopBreakerThreshold,
      numberOfPaymentAttempts,
      paymentCode,
      responseCode,
      dialog,
      linkExpired,
      alreadyPaid,
      generalError,
      donation,
      reset,
      getPaymentCodeParam,
      getResponseCodeParam,
      getGiftCertificateUrl,
      goToPayment,
    };
  },
});
</script>

<style scoped>
.paylink-certificate-card {
  border: 1px solid lightgrey;
  border-radius: 12px;
}
.paylink-about-logo {
  height: 20px;
  float: left;
  color: white;
  padding-right: 4px;
}
.paylink-about-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #E5E7EB;
}
.paylink-about-title {
  font-weight: 600;
  font-family: Lato, sans-serif;
  color: #1f2a37;
  line-height: 29px;
}
</style>
